/**
 * Модель формы отзыва, её проверка и маппинг в запросы API.
 * Проверка повторяет `ReviewContentValidator` бэкенда (Services/Reviews): сначала поля
 * обрезаются, пустые считаются отсутствующими, затем — длина, ссылки, суммарная длина
 * текста и число фото. Бэкенд отдаёт первую найденную ошибку, форма — все сразу.
 */

export type ReviewFormModel = {
  rating: number
  pros: string
  cons: string
  text: string
  hideAuthorName: boolean
  /** id загруженных фото в порядке показа */
  resourceIds: string[]
  /** Конкретная покупка (из «Ожидают оценки» или заказа) — только при создании */
  orderItemId?: number
}

/** Нужные форме поля `ShowcaseReviewsConfigDto` */
export type ReviewFormConfig = {
  allowMedia: boolean
  maxPhotos: number
  allowRatingOnly: boolean
  minTextLength?: number | null
}

/** Нужные форме поля `ShowcaseMyReviewDto` */
export type MyReviewLike = {
  rating: number
  pros?: string | null
  cons?: string | null
  text?: string | null
  hideAuthorName: boolean
  imageIds: string[]
}

export type ReviewFormField = 'rating' | 'pros' | 'cons' | 'text' | 'resourceIds'

/** `ReviewContentValidator.MaxFieldLength` */
export const REVIEW_FIELD_MAX_LENGTH = 2000

/** Тот же шаблон, что `LinkRegex` бэкенда */
const LINK = /(https?:\/\/|www\.)/i

const TEXT_FIELDS = ['pros', 'cons', 'text'] as const

const clean = (value: string) => value.trim() || null

const distinctCount = (ids: string[]) => new Set(ids).size

export function emptyReviewForm(orderItemId?: number): ReviewFormModel {
  const model: ReviewFormModel = { rating: 0, pros: '', cons: '', text: '', hideAuthorName: false, resourceIds: [] }
  if (orderItemId !== undefined) model.orderItemId = orderItemId
  return model
}

export function fromMyReview(review: MyReviewLike): ReviewFormModel {
  return {
    rating: review.rating,
    pros: review.pros ?? '',
    cons: review.cons ?? '',
    text: review.text ?? '',
    hideAuthorName: review.hideAuthorName,
    resourceIds: [...review.imageIds],
  }
}

/** Ошибки по полям формы; пустой объект — можно отправлять. */
export function validateReviewForm(
  model: ReviewFormModel,
  config: ReviewFormConfig,
): Partial<Record<ReviewFormField, string>> {
  const errors: Partial<Record<ReviewFormField, string>> = {}
  if (!Number.isInteger(model.rating) || model.rating < 1 || model.rating > 5) errors.rating = 'Поставьте оценку'

  let totalLength = 0
  for (const key of TEXT_FIELDS) {
    const value = clean(model[key])
    if (value === null) continue
    totalLength += value.length
    if (value.length > REVIEW_FIELD_MAX_LENGTH) errors[key] = `Не больше ${REVIEW_FIELD_MAX_LENGTH} символов`
    else if (LINK.test(value)) errors[key] = 'Ссылки в отзыве запрещены'
  }

  if (!errors.text) {
    if (totalLength === 0 && !config.allowRatingOnly) errors.text = 'Напишите несколько слов о товаре'
    else if (totalLength > 0 && config.minTextLength && totalLength < config.minTextLength)
      errors.text = `Текст отзыва должен быть не короче ${config.minTextLength} символов`
  }

  const photos = distinctCount(model.resourceIds)
  if (photos > 0 && !config.allowMedia) errors.resourceIds = 'Фото в отзывах на этой витрине не принимаются'
  else if (photos > config.maxPhotos) errors.resourceIds = `Не больше ${config.maxPhotos} фото`
  return errors
}

/** Сколько ещё фото можно добавить. */
export function photoSlotsLeft(resourceIds: string[], config: ReviewFormConfig): number {
  if (!config.allowMedia) return 0
  return Math.max(0, config.maxPhotos - distinctCount(resourceIds))
}

/** Тело `POST products/{id}/reviews` (поля `ShowcaseCreateReviewRequest`). */
export function toCreateRequest(model: ReviewFormModel) {
  return {
    rating: model.rating,
    pros: clean(model.pros),
    cons: clean(model.cons),
    text: clean(model.text),
    hideAuthorName: model.hideAuthorName,
    resourceIds: [...model.resourceIds],
    orderItemId: model.orderItemId ?? null,
  }
}

/**
 * Тело `PUT reviews/{id}` (поля `ShowcaseUpdateReviewRequest`): без покупки и без флага имени —
 * имя скрывается отдельным `PUT reviews/{id}/hide-author-name` без повторной модерации.
 */
export function toUpdateRequest(model: ReviewFormModel) {
  return {
    rating: model.rating,
    pros: clean(model.pros),
    cons: clean(model.cons),
    text: clean(model.text),
    resourceIds: [...model.resourceIds],
  }
}

export type ReviewEditPlan = {
  /** Новое значение флага, если он изменился */
  hideAuthorName?: boolean
  /** Тело `PUT reviews/{id}`, если изменилось содержимое */
  content?: ReturnType<typeof toUpdateRequest>
}

/** Фото как их сохраняет бэкенд: уникальные id в порядке показа */
const distinctIds = (ids: string[]) => [...new Set(ids)]

function sameContent(a: ReturnType<typeof toUpdateRequest>, b: ReturnType<typeof toUpdateRequest>) {
  const photosA = distinctIds(a.resourceIds)
  const photosB = distinctIds(b.resourceIds)
  return a.rating === b.rating && a.pros === b.pros && a.cons === b.cons && a.text === b.text
    && photosA.length === photosB.length && photosA.every((id, i) => id === photosB[i])
}

/**
 * Какие запросы нужны для правки: флаг имени уходит отдельным `PUT reviews/{id}/hide-author-name`
 * (без повторной модерации), содержимое — `PUT reviews/{id}` только если оно действительно изменилось,
 * иначе смена одного флага отправила бы отзыв на повторную модерацию.
 */
export function planReviewEdit(initial: ReviewFormModel, model: ReviewFormModel): ReviewEditPlan {
  const plan: ReviewEditPlan = {}
  if (model.hideAuthorName !== initial.hideAuthorName) plan.hideAuthorName = model.hideAuthorName
  const content = toUpdateRequest(model)
  if (!sameContent(toUpdateRequest(initial), content)) plan.content = content
  return plan
}
